import moment from 'moment'
import 'moment-timezone'
import { convertFrom12To24Format } from './convert-24-to-12-format'
import { AppointmentTypeType } from 'app/types/entities'
import { AvailabilityType } from 'app/types/entities'
import { ProviderType } from 'app/types/entities'
import { ResourceSchedule, ResourceScheduleJson } from 'app/types/entities'
import { SchedulingConfigurationJson } from 'app/types/entities'
import { IndexProperties } from 'app/types/entities'
import { log } from 'lib/logging'

// PmsAppointment removed (server-only) — stub for SPA context
type PmsAppointment = { [key: string]: any }

export type PatientStatusType = `New Patient` | `Existing Patient`
export const PATIENT_STATUS_TYPES: PatientStatusType[] = [`New Patient`, `Existing Patient`]

export type PatientData = {
  firstName: string
  lastName: string
  email: string
  phone: string
  dob: string
}

export type TimeSlotType = {
  time: string
  availabilities: AvailabilityType[]
}

export type DaySlotType = {
  timeSlots: TimeSlotType[]
  date: string // format: YYYY-MM-DD
}
// used only by agent, online scheduler uses SchedulingConfiguration.granularity
export const GRANULARITY = 30

export const LOOKAHEAD_DAYS = 31

/**
 * Groups block scheduling notes by appointment type and note text while preserving each note's
 * original boundaries. Adjacent blockout notes are intentionally not merged: a scheduled slot
 * must fit inside one strict blockout note.
 */
function groupStrictBlocks(
  notes: Array<{
    note: string
    startTime: string
    endTime: string
    additionalData?: { appointmentType?: string }
  }>,
): Map<string, Array<{ startTime: string; endTime: string }>> {
  const blocksByType = new Map<string, Array<{ startTime: string; endTime: string }>>()

  if (!notes || !Array.isArray(notes)) {
    return blocksByType
  }

  // Note: OpenDental may return comma-delimited appointment types (e.g., "8,9,10")
  for (const n of notes) {
    const block = { startTime: n.startTime, endTime: n.endTime }
    const appointmentTypeRaw = n.additionalData?.appointmentType

    if (appointmentTypeRaw) {
      const appointmentTypes = appointmentTypeRaw
        .split(`,`)
        .map((t) => t.trim())
        .filter(Boolean)

      for (const appointmentType of appointmentTypes) {
        if (!blocksByType.has(appointmentType)) {
          blocksByType.set(appointmentType, [])
        }
        blocksByType.get(appointmentType)!.push(block)
      }
    }

    // Also key by note text (for blockSchedulingNotes matching)
    if (n.note) {
      const noteKey = `note:${n.note.trim().toLowerCase()}`
      if (!blocksByType.has(noteKey)) {
        blocksByType.set(noteKey, [])
      }
      blocksByType.get(noteKey)!.push(block)
    }
  }

  for (const blocks of blocksByType.values()) {
    blocks.sort((a, b) => a.startTime.localeCompare(b.startTime))
  }

  return blocksByType
}

/**
 * Merges overlapping or touching schedule blocks into continuous intervals
 * @param schedules Array of resource schedules
 * @param date Date string in YYYY-MM-DD format
 * @returns Array of merged time intervals
 */
function mergeScheduleIntervals(
  schedules: ResourceScheduleJson[],
  date: string,
): Array<{ start: moment.Moment; end: moment.Moment }> {
  const intervals: Array<{ start: moment.Moment; end: moment.Moment }> = []

  // Collect all blocks from all schedules
  schedules.forEach((schedule) => {
    schedule.blocks?.forEach((block) => {
      intervals.push({
        start: moment(`${date} ${block.startTime}`, `YYYY-MM-DD HH:mm`),
        end: moment(`${date} ${block.endTime}`, `YYYY-MM-DD HH:mm`),
      })
    })
  })

  if (intervals.length === 0) return []

  // Sort by start time
  intervals.sort((a, b) => a.start.valueOf() - b.start.valueOf())

  // Merge overlapping or touching intervals
  const merged: Array<{ start: moment.Moment; end: moment.Moment }> = []
  let current = intervals[0]

  for (let i = 1; i < intervals.length; i++) {
    const next = intervals[i]
    // If current end is >= next start, merge them
    if (current.end.isSameOrAfter(next.start)) {
      current.end = moment.max(current.end, next.end)
    } else {
      merged.push(current)
      current = next
    }
  }
  merged.push(current)

  return merged
}

/**
 * Filters available appointment time slots by removing ones that conflict with existing appointments,
 * schedules, or business rules. Enforces provider availability, operatory exclusivity, schedule
 * compliance, and block scheduling constraints.
 *
 * @param slots - Available time slots grouped by day. This array is mutated to remove conflicts.
 * @param appointments - Existing appointments from Kolla system to check for conflicts
 * @param schedules - Resource schedules defining when providers/operatories are available
 * @param providers - Provider information with resource names for lookup
 * @param appointmentType - Type of appointment being scheduled (contains duration and block scheduling rules)
 * @param allowDoubleBooking - Whether to allow up to 2 appointments per provider at same time (default: false)
 *
 * @description
 * This function implements comprehensive conflict detection by checking:
 *
 * 1. **Provider Appointment Conflicts**:
 *    - Normal mode: Blocks slots that overlap with existing provider appointments
 *    - Double booking mode: Allows up to 2 overlapping appointments per provider
 *
 * 2. **Operatory Exclusivity**:
 *    - Always enforced regardless of double booking setting
 *    - Blocks slots if operatory has any conflicting appointments
 *
 * 3. **Schedule Availability**:
 *    - Ensures both provider and operatory are scheduled to be open
 *    - Checks that appointment slot falls entirely within available schedule blocks
 *
 * 4. **Block Scheduling**:
 *    - For appointment types requiring block scheduling (appointmentType.blockScheduling = true)
 *    - Ensures appointment slot falls entirely within one designated block for that appointment type
 *    - Preserves original blockout note boundaries so adjacent blocks are not treated as one fluid block
 *
 * @example
 * ```typescript
 * const slots = [
 *   { date: '2023-01-15', timeSlots: [{ time: '9:00 AM', availabilities: [...] }] }
 * ];
 *
 * removeOverlaps(slots, appointments, schedules, providers, appointmentType, false);
 * // slots is now mutated to contain only non-conflicting time slots
 * ```
 *
 * @mutates slots - Removes conflicting availabilities and empty time slots from the input array
 *
 * @performance
 * - Uses O(1) lookup maps for providers, appointments, and schedules
 * - Time complexity: O(S × A × (P + O)) where S=slots, A=availabilities, P=provider appointments, O=operatory appointments
 */
export function removeOverlaps(
  slots: DaySlotType[],
  appointments: PmsAppointment[] | undefined,
  schedules: ResourceScheduleJson[] | undefined,
  providers: ProviderType[],
  appointmentType: AppointmentTypeType | null,
  schedulingConfiguration: SchedulingConfigurationJson | null,
  allowDoubleBooking = false,
) {
  // Early exit if no data to process
  if (!slots.length) return

  // Pre-compute and cache all data structures for O(1) lookups
  // When appointmentType is null (e.g., during initial slot generation for all types),
  // we still need to enforce operatory exclusivity using granularity for overlap checks
  const granularity = schedulingConfiguration?.granularity || GRANULARITY

  log(
    `scheduling`,
    `removeOverlaps:start`,
    JSON.stringify({
      numDays: slots.length,
      numAppointments: appointments?.length ?? 0,
      numSchedules: schedules?.length ?? 0,
      numProviders: providers.length,
      granularity,
      allowDoubleBooking,
    }),
  )

  // Index providers by ID for O(1) lookup
  const providerMap = new Map<string, ProviderType>()
  providers.forEach((provider) => providerMap.set(provider.id!, provider))

  // Index appointments by provider resource name and date for faster lookups
  const appointmentsByProviderAndDate = new Map<string, PmsAppointment[]>()
  // Index appointments by operatory resource name and date for operatory-level occupancy checks
  const appointmentsByOperatoryAndDate = new Map<string, PmsAppointment[]>()
  if (appointments) {
    appointments.forEach((appointment) => {
      appointment.providers?.forEach((provider) => {
        if (!provider.name) return
        const appointmentDate = moment(appointment.wall_start_time).format(`YYYY-MM-DD`)
        const key = `${provider.name!}|${appointmentDate}`
        if (!appointmentsByProviderAndDate.has(key)) {
          appointmentsByProviderAndDate.set(key, [])
        }
        appointmentsByProviderAndDate.get(key)!.push(appointment)
      })

      // Index by operatory. Use appointment.operatory (singular) which is the actual operatory
      // the appointment is booked in. The resources array is unreliable — some PMS integrations
      // return ALL operatories in it, not just the one the appointment occupies.
      const appointmentDate = moment(appointment.wall_start_time).format(`YYYY-MM-DD`)
      const operatory = (appointment as any).operatory as string | undefined
      if (operatory && operatory.startsWith(`resources/operatory_`)) {
        const key = `${operatory}|${appointmentDate}`
        if (!appointmentsByOperatoryAndDate.has(key)) {
          appointmentsByOperatoryAndDate.set(key, [])
        }
        appointmentsByOperatoryAndDate.get(key)!.push(appointment)
      }
    })
  }

  log(
    `scheduling`,
    `removeOverlaps:indexes-built`,
    JSON.stringify({
      appointmentKeys: Array.from(appointmentsByProviderAndDate.keys()).slice(0, 10),
      appointmentKeysCount: appointmentsByProviderAndDate.size,
    }),
  )

  // Index schedules by date and resource name for faster lookups
  const schedulesByDateAndResource = new Map<string, ResourceScheduleJson[]>()
  const operatoryOpenStatus = new Map<string, boolean>() // date|operatoryId -> isOpen

  if (schedules) {
    schedules.forEach((schedule) => {
      if (!schedule.name) return
      const scheduleDate = moment(schedule.date).format(`YYYY-MM-DD`)
      const key = `${scheduleDate}|${schedule.name}`

      if (!schedulesByDateAndResource.has(key)) {
        schedulesByDateAndResource.set(key, [])
      }
      schedulesByDateAndResource.get(key)!.push(schedule)

      // Track operatory open status
      if (schedule.type === `OPERATORY`) {
        const operatoryId = schedule.name.replace(`resources/operatory_`, ``)
        operatoryOpenStatus.set(`${scheduleDate}|${operatoryId}`, true)
      }
    })
  }

  log(
    `scheduling`,
    `removeOverlaps:schedules-indexed`,
    JSON.stringify({
      scheduleKeysSample: Array.from(schedulesByDateAndResource.keys()).slice(0, 10),
      scheduleKeysCount: schedulesByDateAndResource.size,
      operatoryOpenSample: Array.from(operatoryOpenStatus.keys()).slice(0, 10),
      operatoryOpenCount: operatoryOpenStatus.size,
    }),
  )

  // Process each day slot
  slots.forEach((daySlot, dayIndex) => {
    log(
      `scheduling`,
      `removeOverlaps:day-start`,
      JSON.stringify({
        dayIndex,
        date: daySlot.date,
        timeSlots: daySlot.timeSlots.length,
      }),
    )
    daySlot.timeSlots.forEach((timeSlot) => {
      const timeSlot24h = convertFrom12To24Format(timeSlot.time)
      const slotStartMoment = moment(`${daySlot.date} ${timeSlot24h}`, `YYYY-MM-DD HH:mm`)
      const slotEndMoment = slotStartMoment.clone().add(granularity, `minutes`)

      log(
        `scheduling`,
        `removeOverlaps:time-slot`,
        JSON.stringify({
          date: daySlot.date,
          time: timeSlot.time,
          time24h: timeSlot24h,
          slotStart: slotStartMoment.format(),
          slotEnd: slotEndMoment.format(),
          availabilities: timeSlot.availabilities.map((a) => ({
            providerId: a.providerId,
            operatoryId: a.operatoryId,
          })),
        }),
      )

      // Filter availabilities for this time slot
      timeSlot.availabilities = timeSlot.availabilities.filter((availability) => {
        const provider = providerMap.get(availability.providerId!)
        if (!provider) {
          log(
            `scheduling`,
            `removeOverlaps:filter-reason`,
            JSON.stringify({
              date: daySlot.date,
              time: timeSlot.time,
              reason: `provider not found`,
              providerId: availability.providerId,
            }),
          )
          return false
        }

        // Determine the max number of concurrent appointments a provider may have for this slot.
        // Single-booking mode (double booking off, or a type that excludes double booking) caps at 1.
        // Double-booking mode caps at schedulingConfiguration.doubleBookingCount, or is uncapped when
        // that value is null/undefined/0 (operatory exclusivity still bounds concurrency).
        const effectiveDoubleBookingDisabled = !allowDoubleBooking || appointmentType?.doubleBookingExclusion === true
        const doubleBookingCount = schedulingConfiguration?.doubleBookingCount || 0
        const maxConcurrentAppointments = effectiveDoubleBookingDisabled ? 1 : doubleBookingCount
        if (maxConcurrentAppointments > 0) {
          log(
            `scheduling`,
            `removeOverlaps:checking-conflicts`,
            JSON.stringify({
              allowDoubleBooking,
              maxConcurrentAppointments,
              providerId: availability.providerId,
            }),
          )
          if (!provider.resourceName) return false
          const appointmentKey = `${provider.resourceName}|${daySlot.date}`
          const dayAppointments = appointmentsByProviderAndDate.get(appointmentKey)

          log(
            `scheduling`,
            `removeOverlaps:availability-check`,
            JSON.stringify({
              providerId: availability.providerId,
              providerResourceName: provider.resourceName,
              operatoryId: availability.operatoryId,
              appointmentKey,
              dayAppointmentsCount: dayAppointments?.length ?? 0,
            }),
          )

          if (dayAppointments) {
            const overlappingCount = dayAppointments.filter((appointment) => {
              const apptStart = moment(appointment.wall_start_time)
              const apptEnd = moment(appointment.wall_end_time)

              // Provider conflict uses granularity window (coarse filter) - detailed duration
              // validation happens client-side. Using full appointment duration would over-count
              // sequential provider appointments when double booking is enabled.
              const overlap = slotEndMoment.isAfter(apptStart) && slotStartMoment.isBefore(apptEnd)
              if (overlap) {
                log(
                  `scheduling`,
                  `removeOverlaps:appointment-overlap`,
                  JSON.stringify({
                    providerResourceName: provider.resourceName,
                    appointment: {
                      wall_start_time: appointment.wall_start_time,
                      wall_end_time: appointment.wall_end_time,
                    },
                    slotStart: slotStartMoment.format(),
                    slotEnd: slotEndMoment.format(),
                  }),
                )
              }
              return overlap
            }).length

            if (overlappingCount >= maxConcurrentAppointments) {
              log(
                `scheduling`,
                `removeOverlaps:filter-reason`,
                JSON.stringify({
                  date: daySlot.date,
                  time: timeSlot.time,
                  reason: `provider at concurrent booking limit`,
                  overlappingCount,
                  maxConcurrentAppointments,
                  providerId: availability.providerId,
                  operatoryId: availability.operatoryId,
                }),
              )
              return false
            }
          }
        }
        // When double booking is uncapped, we don't check provider-level conflicts.
        // The operatory exclusivity check below ensures no double-booking of operatories.
        // If a provider is configured for multiple operatories, they can have appointments
        // in different operatories at the same time (scheduled by the dental office in their PMS).

        // Check operatory appointment conflicts (always enforced for operatory exclusivity)
        if (availability.operatoryId) {
          const operatoryResourceName = `resources/operatory_${availability.operatoryId}`
          const operatoryAppointmentKey = `${operatoryResourceName}|${daySlot.date}`
          const operatoryAppointments = appointmentsByOperatoryAndDate.get(operatoryAppointmentKey)

          log(
            `scheduling`,
            `removeOverlaps:operatory-appointment-check`,
            JSON.stringify({
              operatoryAppointmentKey,
              operatoryAppointmentsCount: operatoryAppointments?.length ?? 0,
            }),
          )

          if (operatoryAppointments) {
            const hasOperatoryAppointmentConflict = operatoryAppointments.some((appointment) => {
              const apptStart = moment(appointment.wall_start_time)
              const apptEnd = moment(appointment.wall_end_time)
              // Operatory conflict uses granularity window (coarse filter) - detailed duration
              // validation happens client-side for the scheduler path.
              const overlap = slotEndMoment.isAfter(apptStart) && slotStartMoment.isBefore(apptEnd)
              if (overlap) {
                log(
                  `scheduling`,
                  `removeOverlaps:operatory-overlap`,
                  JSON.stringify({
                    operatoryResourceName,
                    appointment: {
                      id: (appointment as any).remote_id,
                      wall_start_time: appointment.wall_start_time,
                      wall_end_time: appointment.wall_end_time,
                      short_description: (appointment as any).short_description,
                      contact: (appointment as any).contact,
                    },
                    slotStart: slotStartMoment.format(),
                    slotEnd: slotEndMoment.format(),
                  }),
                )
              }
              return overlap
            })

            if (hasOperatoryAppointmentConflict) {
              log(
                `scheduling`,
                `removeOverlaps:filter-reason`,
                JSON.stringify({
                  date: daySlot.date,
                  time: timeSlot.time,
                  reason: `operatory has conflicting appointment`,
                  providerId: availability.providerId,
                  operatoryId: availability.operatoryId,
                }),
              )
              return false
            }
          }
        }

        // Check if operatory is open - this check applies regardless of double booking setting
        const operatoryKey = `${daySlot.date}|${availability.operatoryId}`
        const isOperatoryOpen = operatoryOpenStatus.has(operatoryKey)
        log(`scheduling`, `removeOverlaps:operatory-open-check`, JSON.stringify({ operatoryKey, isOperatoryOpen }))
        if (!isOperatoryOpen) {
          log(
            `scheduling`,
            `removeOverlaps:filter-reason`,
            JSON.stringify({
              date: daySlot.date,
              time: timeSlot.time,
              reason: `operatory not open`,
              providerId: availability.providerId,
              operatoryId: availability.operatoryId,
              operatoryKey,
            }),
          )
          return false
        }

        // Check provider schedule conflicts
        const providerScheduleKey = `${daySlot.date}|${provider.resourceName}`
        const providerSchedules = schedulesByDateAndResource.get(providerScheduleKey)

        if (providerSchedules) {
          const mergedIntervals = mergeScheduleIntervals(providerSchedules, daySlot.date)
          const hasProviderScheduleConflict =
            mergedIntervals.length > 0
              ? !mergedIntervals.some(
                  ({ start, end }) => slotStartMoment.isSameOrAfter(start) && slotEndMoment.isSameOrBefore(end),
                )
              : true

          log(
            `scheduling`,
            `removeOverlaps:provider-schedule-check`,
            JSON.stringify({
              providerScheduleKey,
              providerSchedulesCount: providerSchedules?.length ?? 0,
              mergedIntervals: mergedIntervals.map((m) => ({
                start: m.start.format(`HH:mm`),
                end: m.end.format(`HH:mm`),
              })),
              hasProviderScheduleConflict,
            }),
          )
          if (hasProviderScheduleConflict) {
            log(
              `scheduling`,
              `removeOverlaps:filter-reason`,
              JSON.stringify({
                date: daySlot.date,
                time: timeSlot.time,
                reason: `provider schedule conflict`,
                providerId: availability.providerId,
                operatoryId: availability.operatoryId,
                providerScheduleKey,
              }),
            )
            return false
          }
        }

        // Check operatory schedule conflicts
        const operatoryScheduleKey = `${daySlot.date}|resources/operatory_${availability.operatoryId}`
        const operatorySchedules = schedulesByDateAndResource.get(operatoryScheduleKey)

        if (operatorySchedules) {
          const mergedIntervals = mergeScheduleIntervals(operatorySchedules, daySlot.date)
          const hasOperatoryScheduleConflict =
            mergedIntervals.length > 0
              ? !mergedIntervals.some(
                  ({ start, end }) => slotStartMoment.isSameOrAfter(start) && slotEndMoment.isSameOrBefore(end),
                )
              : true

          log(
            `scheduling`,
            `removeOverlaps:operatory-schedule-check`,
            JSON.stringify({
              operatoryScheduleKey,
              operatorySchedulesCount: operatorySchedules?.length ?? 0,
              mergedIntervals: mergedIntervals.map((m) => ({
                start: m.start.format(`HH:mm`),
                end: m.end.format(`HH:mm`),
              })),
              hasOperatoryScheduleConflict,
            }),
          )
          if (hasOperatoryScheduleConflict) {
            log(
              `scheduling`,
              `removeOverlaps:filter-reason`,
              JSON.stringify({
                date: daySlot.date,
                time: timeSlot.time,
                reason: `operatory schedule conflict`,
                providerId: availability.providerId,
                operatoryId: availability.operatoryId,
                operatoryScheduleKey,
                scheduleBlocks: operatorySchedules?.flatMap((s) => s.blocks),
                slotStart: slotStartMoment.format(),
                slotEnd: slotEndMoment.format(),
              }),
            )
            return false
          }
        }

        // Check block scheduling constraints (skip if appointmentType is null)
        if (appointmentType?.blockScheduling && availability.operatoryId && operatorySchedules) {
          // Note: Resource schedule notes store vendorId (e.g., "8"), not full ID (e.g., "appointmenttypes/8")
          const vendorId = appointmentType.vendorId!
          let hasValidBlock = false

          for (const schedule of operatorySchedules) {
            if (schedule.notes && Array.isArray(schedule.notes)) {
              const strictBlocks = groupStrictBlocks(schedule.notes)
              log(
                `scheduling`,
                `removeOverlaps:checking-blocks`,
                JSON.stringify({
                  vendorId,
                  operatoryId: availability.operatoryId,
                  date: daySlot.date,
                  time: timeSlot.time,
                  slotStart: slotStartMoment.format(),
                  slotEnd: slotEndMoment.format(),
                  allBlockKeys: Array.from(strictBlocks.keys()),
                }),
              )
              // Try matching with just vendor ID first (e.g., "8"), then with full format (e.g., "appointmenttypes/8")
              const blocksForType = strictBlocks.get(vendorId) || strictBlocks.get(`appointmenttypes/${vendorId}`)

              // Also collect blocks matched by note text via blockSchedulingNotes
              const noteBlocks: Array<{ startTime: string; endTime: string }> = []
              if (appointmentType.blockSchedulingNotes?.length) {
                for (const noteRef of appointmentType.blockSchedulingNotes) {
                  const key = noteRef.trim().toLowerCase()
                  const matched = strictBlocks.get(key)
                  if (matched) noteBlocks.push(...matched)
                }
              }

              const allBlocks = [...(blocksForType || []), ...noteBlocks]

              if (allBlocks.length) {
                log(
                  `scheduling`,
                  `removeOverlaps:blocks-found`,
                  JSON.stringify({
                    vendorId,
                    blocksCount: allBlocks.length,
                    blocks: allBlocks.map((b) => ({ start: b.startTime, end: b.endTime })),
                  }),
                )
                hasValidBlock = allBlocks.some((block) => {
                  const blockStart = moment(`${daySlot.date} ${block.startTime}`, `YYYY-MM-DD HH:mm:ss`)
                  const blockEnd = moment(`${daySlot.date} ${block.endTime}`, `YYYY-MM-DD HH:mm:ss`)

                  const matches = slotStartMoment.isSameOrAfter(blockStart) && slotEndMoment.isSameOrBefore(blockEnd)
                  log(
                    `scheduling`,
                    `removeOverlaps:block-match-check`,
                    JSON.stringify({
                      blockStart: blockStart.format(),
                      blockEnd: blockEnd.format(),
                      slotStart: slotStartMoment.format(),
                      slotEnd: slotEndMoment.format(),
                      matches,
                    }),
                  )
                  // Slot must fit within the designated block
                  return matches
                })

                if (hasValidBlock) break
              } else {
                log(
                  `scheduling`,
                  `removeOverlaps:no-blocks-for-type`,
                  JSON.stringify({
                    vendorId,
                    alternateKey: `appointmenttypes/${vendorId}`,
                  }),
                )
              }
            }
          }

          if (!hasValidBlock) {
            log(
              `scheduling`,
              `removeOverlaps:block-scheduling-violation`,
              JSON.stringify({
                vendorId,
                operatoryId: availability.operatoryId,
                slotStart: slotStartMoment.format(),
                slotEnd: slotEndMoment.format(),
              }),
            )
            log(
              `scheduling`,
              `removeOverlaps:filter-reason`,
              JSON.stringify({
                date: daySlot.date,
                time: timeSlot.time,
                reason: `block scheduling violation`,
                providerId: availability.providerId,
                operatoryId: availability.operatoryId,
                vendorId,
              }),
            )
            return false
          }
        }

        // Check blockout exclusions (mutually exclusive with block scheduling)
        // excludedBlockouts contains:
        //   - appointment type refs like "appointmenttypes/3"
        //   - note name refs like "note:S/RP"
        // Skip if appointmentType is null (no exclusions to check)
        if (
          appointmentType &&
          !appointmentType.blockScheduling &&
          appointmentType.excludedBlockouts?.length &&
          availability.operatoryId &&
          operatorySchedules
        ) {
          // Partition into appointment-type refs and note-name refs
          const excludedApptTypes: string[] = []
          const excludedNoteNames: string[] = []
          for (const ref of appointmentType.excludedBlockouts) {
            if (ref.startsWith(`note:`)) {
              excludedNoteNames.push(ref.slice(5).toLowerCase())
            } else {
              excludedApptTypes.push(ref)
            }
          }

          for (const schedule of operatorySchedules) {
            if (schedule.notes && Array.isArray(schedule.notes)) {
              for (const blockout of schedule.notes) {
                // Check appointment-type-based exclusion (existing behavior)
                let isExcluded = false
                const blockoutApptTypeRaw = blockout.additionalData?.appointmentType
                if (blockoutApptTypeRaw && excludedApptTypes.length) {
                  const blockoutApptTypes = blockoutApptTypeRaw
                    .split(`,`)
                    .map((t) => t.trim())
                    .filter(Boolean)

                  isExcluded = blockoutApptTypes.some((blockoutApptType) => {
                    const blockoutRef = blockoutApptType.startsWith(`appointmenttypes/`)
                      ? blockoutApptType
                      : `appointmenttypes/${blockoutApptType}`
                    return excludedApptTypes.includes(blockoutRef)
                  })
                }

                // Check note-name-based exclusion
                if (!isExcluded && excludedNoteNames.length && blockout.note) {
                  isExcluded = excludedNoteNames.includes(blockout.note.toLowerCase())
                }

                if (!isExcluded) continue
                if (!blockout.startTime || !blockout.endTime) continue

                const blockoutStart = moment(`${daySlot.date} ${blockout.startTime}`, `YYYY-MM-DD HH:mm:ss`)
                const blockoutEnd = moment(`${daySlot.date} ${blockout.endTime}`, `YYYY-MM-DD HH:mm:ss`)

                // Slot would overlap blockout if: slotEnd > blockoutStart AND slotStart < blockoutEnd
                if (slotEndMoment.isAfter(blockoutStart) && slotStartMoment.isBefore(blockoutEnd)) {
                  log(
                    `scheduling`,
                    `removeOverlaps:blockout-exclusion`,
                    JSON.stringify({
                      blockoutNote: blockout.note,
                      blockoutApptType: blockoutApptTypeRaw,
                      blockoutStart: blockoutStart.format(),
                      blockoutEnd: blockoutEnd.format(),
                      slotStart: slotStartMoment.format(),
                      slotEnd: slotEndMoment.format(),
                    }),
                  )
                  log(
                    `scheduling`,
                    `removeOverlaps:filter-reason`,
                    JSON.stringify({
                      date: daySlot.date,
                      time: timeSlot.time,
                      reason: `blockout exclusion`,
                      providerId: availability.providerId,
                      operatoryId: availability.operatoryId,
                      blockoutNote: blockout.note,
                      blockoutApptType: blockoutApptTypeRaw,
                    }),
                  )
                  return false
                }
              }
            }
          }
        }

        return true
      })

      log(
        `scheduling`,
        `removeOverlaps:time-slot-after-filter`,
        JSON.stringify({
          date: daySlot.date,
          time: timeSlot.time,
          remainingAvailabilityProviders: timeSlot.availabilities.map((a) => a.providerId),
        }),
      )
    })

    // Filter out empty time slots and sort by time
    daySlot.timeSlots = daySlot.timeSlots
      .filter((timeSlot) => timeSlot.availabilities.length > 0)
      .sort((a, b) => {
        const timeA = moment(a.time, `h:mm A`)
        const timeB = moment(b.time, `h:mm A`)
        return timeA.valueOf() - timeB.valueOf()
      })

    log(
      `scheduling`,
      `removeOverlaps:day-after-filter`,
      JSON.stringify({
        dayIndex,
        date: daySlot.date,
        remainingTimes: daySlot.timeSlots.map((t) => t.time),
      }),
    )
  })
}

export type SchedulingLoaderData = {
  day: string
  lookaheadDays: number
  appointmentTypes: AppointmentTypeType[]
  logoUrl: Promise<string>
  providersPromise: Promise<ProviderType[]>
  availabilitiesPromise: Promise<AvailabilityType[]>
  internalSchedulesPromise: Promise<IndexProperties<ResourceSchedule>[]>
  externalAppointmentsPromise: Promise<Partial<PmsAppointment>[]>
  schedulingConfiguration: SchedulingConfigurationJson | null
  timing: Record<string, number>
}

export type ProcessedSchedulingData = {
  day: string
  lookaheadDays: number
  appointmentTypes: AppointmentTypeType[]
  logoUrl: Promise<string>
  providers: ProviderType[]
  availabilities: AvailabilityType[]
  slots: DaySlotType[]
  schedulingConfiguration: SchedulingConfigurationJson | null
  timing: Record<string, number>
}

export type SchedulingDataApiResponse = Omit<ProcessedSchedulingData, `appointmentTypes` | `logoUrl`> & {
  // appointmentTypes load from the fast public-appointment-types endpoint, not this payload.
  // logoUrl is resolved to a string (or null) in the JSON API response
  logoUrl?: string | null
  // "error" means the scheduler could not load Kolla data.
  pmsStatus?: `ok` | `error`
  pmsError?: string
}
